import { useState, useCallback } from 'react';
import {
  Cms1500Form as Cms1500FormType,
  createEmptyCMS1500Form,
  upsertCMS1500Forms,
} from '../../../services/cms1500Service';
import { Severity } from '../../../types';

interface UseCMS1500FormManagementProps {
  showSnackbar: (message: string, severity: Severity) => void;
  selectedClaimId: string | null;
}

/**
 * Hook for managing CMS1500 form state and operations
 */
export const useCMS1500FormManagement = ({
  showSnackbar,
  selectedClaimId,
}: UseCMS1500FormManagementProps) => {
  // CMS-1500 form state
  const [cms1500Forms, setCms1500Forms] = useState<Cms1500FormType[]>([]);
  const [currentCms1500FormIndex, setCurrentCms1500FormIndex] = useState<number>(0);

  /**
   * Get the current form
   */
  const getCurrentForm = useCallback(() => {
    return cms1500Forms.length > 0 ? cms1500Forms[currentCms1500FormIndex] : null;
  }, [cms1500Forms, currentCms1500FormIndex]);

  /**
   * Get all forms
   */
  const getCurrentForms = useCallback(() => {
    return cms1500Forms;
  }, [cms1500Forms]);

  /**
   * Get the current form index
   */
  const getCurrentFormIndex = useCallback(() => {
    return currentCms1500FormIndex;
  }, [currentCms1500FormIndex]);

  /**
   * Change the current form index
   */
  const changeFormIndex = useCallback((index: number) => {
    setCurrentCms1500FormIndex(index);
  }, []);

  /**
   * Handle CMS-1500 form changes
   */
  const handleFormChange = useCallback(
    (updatedForm: Cms1500FormType) => {
      const currentForm = cms1500Forms[currentCms1500FormIndex];
      // check if the form has changed
      if (JSON.stringify(currentForm) === JSON.stringify(updatedForm)) {
        return false; // No changes detected
      }
      const updatedForms = [...cms1500Forms];
      updatedForms[currentCms1500FormIndex] = updatedForm;
      setCms1500Forms(updatedForms);
      return true; // Indicates changes were made
    },
    [cms1500Forms, currentCms1500FormIndex]
  );

  /**
   * Add a new CMS-1500 form
   */
  const addNewForm = useCallback(
    (policyNumber = '') => {
      const newForm = createEmptyCMS1500Form(policyNumber);
      const updatedForms = [...cms1500Forms, newForm];
      setCms1500Forms(updatedForms);
      setCurrentCms1500FormIndex(updatedForms.length - 1);
      showSnackbar('New CMS-1500 form added', Severity.success);
    },
    [cms1500Forms, showSnackbar]
  );

  /**
   * Handle selecting a CMS-1500 claim from search
   */
  const selectClaim = useCallback((selectedForm: Cms1500FormType) => {
    setCms1500Forms([selectedForm]);
    setCurrentCms1500FormIndex(0);
  }, []);

  /**
   * Save the current form
   */
  const saveCurrentForm = useCallback(async () => {
    try {
      if (cms1500Forms.length > 0) {
        // Save the form to the backend
        await upsertCMS1500Forms(cms1500Forms);

        // Update the form's dirty state
        const currentForm = cms1500Forms[currentCms1500FormIndex];
        const updatedForms = [...cms1500Forms];
        updatedForms[currentCms1500FormIndex] = {
          ...currentForm,
          isDirty: false,
        };
        setCms1500Forms(updatedForms);

        showSnackbar('CMS-1500 form saved successfully', Severity.success);
      }
    } catch (error) {
      console.error('Error saving CMS-1500 form:', error);
      showSnackbar('Error saving CMS-1500 form. Please try again.', Severity.error);
      throw error;
    }
  }, [cms1500Forms, currentCms1500FormIndex, showSnackbar]);

  /**
   * Handle proceeding with a pending action
   */
  const handleProceedWithAction = useCallback(
    (action: { type: string; payload?: any }) => {
      switch (action.type) {
        case 'changeFormTab':
          setCurrentCms1500FormIndex(action.payload.index);
          break;
        case 'addForm':
          addNewForm();
          break;
        case 'selectClaim':
          setCms1500Forms(action.payload);
          setCurrentCms1500FormIndex(0);
          break;
      }
    },
    [addNewForm]
  );

  return {
    cms1500Forms,
    currentCms1500FormIndex,
    getCurrentForm,
    getCurrentForms,
    getCurrentFormIndex,
    changeFormIndex,
    handleFormChange,
    addNewForm,
    selectClaim,
    saveCurrentForm,
    handleProceedWithAction,
  };
};

export default useCMS1500FormManagement;
